import { Button, Menu, MenuItem, Stack, SvgIcon, Typography } from '@mui/material'
import { IconChevronDown } from '@tabler/icons-react'
import { ChangeEvent, useCallback, useRef, useState } from 'react'
import { getZodErrorMessage } from '../../../shared/utils'
import { toast } from 'sonner'
import * as XLSX from 'xlsx'
import { z, ZodType } from 'zod'
import { useDownload } from '~/features/hooks/useDownload'

type UseImportProps<SchemaT extends ZodType> = {
  schema: SchemaT
  sample?: Record<string, unknown>[]
}

export const useImport = <
  SchemaT extends ZodType,
  DataT extends z.infer<SchemaT> = z.infer<SchemaT>,
>({
  schema,
  sample,
}: UseImportProps<SchemaT>) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [excelData, setExcelData] = useState<DataT | null>(null)

  // Programmatically click the hidden file input when the button is clicked
  const handleButtonClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleImport = useCallback(
    (file: File) => {
      const reader = new FileReader()
      reader.onload = (event) => {
        try {
          const { result } = event.target ?? {}
          if (!result) throw Error('FileReader error: event.target.result is undefined')

          const workbook = XLSX.read(result, { type: 'binary' })

          // Get first worksheet
          const worksheetName = workbook.SheetNames[0]
          if (!worksheetName) throw Error('Unexpected error: worksheetName is undefined')

          const worksheet = workbook.Sheets[worksheetName]

          if (!worksheet) throw Error('Unexpected error: worksheet is undefined')
          // Convert to JSON
          const data = XLSX.utils.sheet_to_json(worksheet)

          const parsed = schema.safeParse(data)
          if (parsed.error) {
            console.warn(parsed.error, data)
            const message = getZodErrorMessage(parsed.error)

            if (message) toast.warning(message)
            return
          }

          setExcelData(parsed.data)
        } catch (error) {
          console.error('Error importing Excel:', error)
          toast.error('Error importing Excel. Check console')
        }
      }
      reader.readAsBinaryString(file)
    },
    [schema]
  )

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]

      if (!file) return

      handleImport(file)

      // Reset the input value to allow selecting the same file again
      e.target.value = ''
    },
    [handleImport]
  )

  const ImportButton = useCallback(
    ({ label, onClick }: { label: string; onClick?: () => void }) => (
      <Stack direction="row" sx={{ gap: 0.5 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx, .xls, .csv"
          onChange={handleFileChange}
          style={{ display: 'none' }} // Hide the native input
        />

        <Button
          onClick={() => {
            handleButtonClick()
            onClick?.()
          }}
          variant="contained"
          sx={{ width: 1, height: 1, textAlign: 'inherit' }}
        >
          {label}
        </Button>
        {sample ? <ImportMenu sample={sample} /> : null}
      </Stack>
    ),
    [handleButtonClick, handleFileChange, sample]
  )

  const reset = useCallback(() => {
    setExcelData(null)
  }, [])

  return { ImportButton, excelData, reset }
}

const ImportMenu = ({ sample }: { sample: Record<string, unknown>[] }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const { handleDownload } = useDownload()

  const downloadImportFile = () => {
    const worksheet = XLSX.utils.json_to_sheet(sample)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sample')
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    handleDownload(new Blob([excelBuffer]), 'import-sample.xlsx')
    setAnchorEl(null)
  }

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        variant="contained"
        sx={{ px: 1, minWidth: 40 }}
      >
        <SvgIcon fontSize="small">
          <IconChevronDown />
        </SvgIcon>
      </Button>
      <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={downloadImportFile}>
          <Typography variant="body2">Download sample</Typography>
        </MenuItem>
      </Menu>
    </>
  )
}
